// ==========================================
// FILE: \src\screens\CaveauScreen.web.js
// ==========================================

import React, { useState, useEffect, Suspense, useMemo } from 'react';
import { 
  View, Text, StyleSheet, TouchableOpacity, ScrollView, 
  ActivityIndicator, Modal, TextInput, KeyboardAvoidingView, Platform, useWindowDimensions 
} from 'react-native';
import { useWineContext } from '../context/WineContext'; 
import { callGemini } from '../utils/geminiClient'; 
import EnergyHeader from '../components/EnergyHeader'; 
import { 
  Plus, Wine, Trash2, LayoutGrid, Minus, ArrowLeft, Box, 
  AlertCircle, Info, CheckCircle2, AlignVerticalSpaceAround, Rows3, 
  Search, X, ChevronRight, Sparkles 
} from 'lucide-react-native';

import OutOfEnergyModal from '../components/OutOfEnergyModal';
import RewardedAdModal from '../components/RewardedAdModal';

// --- LIBRERIE 3D VERSIONE WEB --- 
import { Canvas } from '@react-three/fiber'; 
import { OrbitControls, useGLTF, Environment, useProgress } from '@react-three/drei';

useGLTF.preload(require('../../assets/red_bottle.glb'));
useGLTF.preload(require('../../assets/spark_bottle.glb'));
useGLTF.preload(require('../../assets/white_bottle.glb'));

// ==========================================
// COMPONENTI 3D CON RENDERING FISICO AVANZATO (INALTERATI)
// ==========================================

const Bottle3D = ({ position, type, isVertical, onClick, searchActive, isHighlighted }) => {
  const gltfRed = useGLTF(require('../../assets/red_bottle.glb'));
  const gltfSpark = useGLTF(require('../../assets/spark_bottle.glb'));
  const gltfWhite = useGLTF(require('../../assets/white_bottle.glb'));

  let selectedGltf;
  let selectedScale;
  let selectedPosition;
  let selectedRotation;

  if (type === 'Rosso') {
    selectedGltf = gltfRed;
    if (isVertical) {
      selectedScale = [0.45, 0.45, 0.45];      
      selectedPosition = [0, 1.45, 0];             
      selectedRotation = [0, 0, 0];             
    } else {
      selectedScale = [0.45, 0.45, 0.45];      
      selectedPosition = [0, -0.01, -0.05];     
      selectedRotation = [Math.PI / 2, 2, 0];  
    }
  } else if (type === 'Bollicine') {
    selectedGltf = gltfSpark;
    if (isVertical) {
      selectedScale = [1.55, 1.55, 1.55];      
      selectedPosition = [0, 1.55, 0];             
      selectedRotation = [0, 1.2, 0];             
    } else {
      selectedScale = [1.55, 1.55, 1.55];      
      selectedPosition = [0, -0.01, 0.05];      
      selectedRotation = [Math.PI / 2, 0, 0];  
    }
  } else {
    selectedGltf = gltfWhite;
    if (isVertical) {
      selectedScale = [10, 10, 10];             
      selectedPosition = [0, 0, 0];             
      selectedRotation = [0, 0, 0];             
    } else {
      selectedScale = [10, 10, 10];             
      selectedPosition = [0, -0.05, -1.5];      
      selectedRotation = [Math.PI / 2, 0, 0];  
    }
  }

  const clonedScene = useMemo(() => {
    const scene = selectedGltf.scene.clone();
    
    scene.traverse((child) => {
      if (child.isMesh) {
        child.material = child.material.clone();
        child.material.roughness = 0.15; 
        child.material.metalness = 0.5;  
        
        if (child.material.transmission !== undefined && child.material.transmission > 0) {
          child.material.transmission = 0.3; 
          child.material.transparent = true;
          child.material.opacity = 0.85; 
        }

        if (searchActive) {
          if (isHighlighted) {
            child.material.transparent = false;
            child.material.opacity = 1;
            child.material.emissive?.setHex(0x1a0505); 
            child.material.emissiveIntensity = 0.25;
          } else {
            child.material.transparent = true;
            child.material.opacity = 0.12;
            child.material.emissive?.setHex(0x000000);
            child.material.emissiveIntensity = 0;
            if (child.material.transmission !== undefined) {
              child.material.transmission = 0;
            }
          }
        }
      }
    });
    return scene;
  }, [selectedGltf.scene, searchActive, isHighlighted]);

  return (
    <group position={position} onClick={onClick}>
      <primitive object={clonedScene} rotation={selectedRotation} scale={selectedScale} position={selectedPosition} />
    </group>
  );
};

const Shelf3D = ({ activeLocation, presentWines, onSlotPress, searchResultIds }) => {
  if (!activeLocation) return null;
  
  const isVertical = activeLocation.type === 'vertical';
  const isSearchActive = searchResultIds && searchResultIds.length > 0;
  
  const rows = activeLocation.rows || 3;
  const cols = activeLocation.cols || 4;
  const depth = activeLocation.depth || (isVertical ? 2 : 1);

  const cellW = 1.1; 
  const cellD = isVertical ? 1.1 : 2.0; 
  const cellH = isVertical ? 3.4 : 1.1; 

  const startX = -((cols - 1) * cellW) / 2;
  const startY = ((rows - 1) * cellH) / 2;
  const startZ = 0;

  const elements = [];
  const woods = [];
  const woodColor = isSearchActive ? "#334155" : "#d4d4d8";
  const woodMaterial = <meshStandardMaterial color={woodColor} roughness={0.7} metalness={0.1} />;

  if (isVertical) {
    for (let r = 0; r <= rows; r++) {
      woods.push(
        <mesh key={`v-wood-r-${r}`} position={[0, startY - r * cellH + (cellH / 2), startZ - (depth * cellD) / 2 + (cellD / 2)]}>
          <boxGeometry args={[cols * cellW, 0.05, depth * cellD]} />
          {woodMaterial}
        </mesh>
      );
    }
    woods.push(
      <mesh key={`v-wood-left`} position={[startX - cellW / 2, 0, startZ - (depth * cellD) / 2 + (cellD / 2)]}>
        <boxGeometry args={[0.05, rows * cellH, depth * cellD]} />
        {woodMaterial}
      </mesh>,
      <mesh key={`v-wood-right`} position={[startX + cols * cellW - cellW / 2, 0, startZ - (depth * cellD) / 2 + (cellD / 2)]}>
        <boxGeometry args={[0.05, rows * cellH, depth * cellD]} />
        {woodMaterial}
      </mesh>
    );

    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        for (let d = 0; d < depth; d++) {
          const posX = startX + c * cellW;
          const posY = startY - r * cellH;
          const posZ = startZ - d * cellD;
          const floorY = posY - (cellH / 2) + 0.025; 

          const slot = activeLocation.slots?.find(s => s.row === r && s.col === c && (s.depth || 0) === d);
          const wineObj = slot ? presentWines.find(w => w.id === slot.wineId) : null;
          const isHighlighted = isSearchActive && wineObj && searchResultIds.includes(wineObj.id);

          elements.push(
            <mesh key={`slot-${r}-${c}-${d}`} position={[posX, floorY + 0.1, posZ]} onClick={(e) => { e.stopPropagation(); onSlotPress(r, c, d); }}>
              <boxGeometry args={[cellW - 0.2, 0.2, cellD - 0.2]} />
              <meshPhysicalMaterial color="#ffffff" transmission={0.9} transparent opacity={0.3} roughness={0.1} clearcoat={1} depthWrite={false} />
            </mesh>
          );

          if (slot && wineObj) {
            elements.push(
              <Bottle3D key={`bottle-${r}-${c}-${d}`} position={[posX, floorY, posZ]} type={wineObj.type} isVertical={true} searchActive={isSearchActive} isHighlighted={isHighlighted} onClick={(e) => { e.stopPropagation(); onSlotPress(r, c, d); }} />
            );
          }
        }
      }
    }
  } else {
    for (let r = 0; r <= rows; r++) {
      woods.push(
        <mesh key={`h-wood-${r}`} position={[0, startY - r * cellH + (cellH / 2), -cellD/2]}>
          <boxGeometry args={[cols * cellW, 0.05, cellD]} />
          {woodMaterial}
        </mesh>
      );
    }
    for (let c = 0; c <= cols; c++) {
      woods.push(
        <mesh key={`v-wood-${c}`} position={[startX + c * cellW - (cellW / 2), 0, -cellD/2]}>
          <boxGeometry args={[0.05, rows * cellH, cellD]} />
          {woodMaterial}
        </mesh>
      );
    }

    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const posX = startX + c * cellW;
        const posY = startY - r * cellH;
        const slot = activeLocation.slots?.find(s => s.row === r && s.col === c && (!s.depth || s.depth === 0));
        const wineObj = slot ? presentWines.find(w => w.id === slot.wineId) : null;
        const isHighlighted = isSearchActive && wineObj && searchResultIds.includes(wineObj.id);

        elements.push(
          <mesh key={`slot-${r}-${c}-0`} position={[posX, posY, -cellD/2]} onClick={(e) => { e.stopPropagation(); onSlotPress(r, c, 0); }}>
            <boxGeometry args={[cellW - 0.1, cellH - 0.1, cellD - 0.1]} />
            <meshPhysicalMaterial color="#ffffff" transmission={0.9} transparent opacity={0.3} roughness={0.1} clearcoat={1} depthWrite={false} />
          </mesh>
        );

        if (slot && wineObj) {
          elements.push(
            <Bottle3D key={`bottle-${r}-${c}-0`} position={[posX, posY, -0.5]} type={wineObj.type} isVertical={false} searchActive={isSearchActive} isHighlighted={isHighlighted} onClick={(e) => { e.stopPropagation(); onSlotPress(r, c, 0); }} />
          );
        }
      }
    }
  }

  return <group>{woods}{elements}</group>;
};

// ==========================================
// COMPONENTE CORE PRINCIPALE WINDOWS/WEB
// ==========================================
export default function CaveauScreen({ route, navigation }) {
  const { locations, addLocationLocal, updateLocationLocal, deleteLocationLocal, presentWines, getUnplacedWines, consumeEnergy, updateWineLocal, userProfile, addEnergy, t } = useWineContext();
  const isPremium = userProfile?.isPremium || false;

  const { width } = useWindowDimensions();
  const isDesktop = width >= 1024; 

  const [activeLocId, setActiveLocId] = useState(null);
  const [selectedWine, setSelectedWine] = useState(null);
  const [unplacedWines, setUnplacedWines] = useState([]);
  const [isInitializing, setIsInitializing] = useState(true);

  const { active, progress } = useProgress();
  const [activeSidebar, setActiveSidebar] = useState('none');

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false); 

  const [newLocName, setNewLocName] = useState('');
  const [newLocType, setNewLocType] = useState('horizontal');
  const [newLocTemp, setNewLocTemp] = useState(14); 

  const [editLocName, setEditLocName] = useState('');
  const [editLocType, setEditLocType] = useState('horizontal');
  const [editLocTemp, setEditLocTemp] = useState(14);

  const [newRows, setNewRows] = useState(3);
  const [newCols, setNewCols] = useState(4);

  const [isAiRecommending, setIsAiRecommending] = useState(false);
  const [showOutOfEnergy, setShowOutOfEnergy] = useState(false);
  const [showAdModal, setShowAdModal] = useState(false);

  const [searchQuery, setSearchQuery] = useState('');
  const [activeSearchQuery, setActiveSearchQuery] = useState('');
  const [searchResultIds, setSearchResultIds] = useState([]);
  
  const searchSuggestions = useMemo(() => {
    if (!searchQuery.trim() || searchQuery.length < 2) return [];
    const lowerQ = searchQuery.toLowerCase();
    return presentWines.filter(w => w.name.toLowerCase().includes(lowerQ) || w.type.toLowerCase().includes(lowerQ));
  }, [searchQuery, presentWines]);

  const [slotActionModal, setSlotActionModal] = useState({ visible: false, wine: null, slot: null });
  const [customAlert, setCustomAlert] = useState({ visible: false, title: '', message: '', type: 'info', onConfirm: null, confirmText: 'OK', cancelText: 'Annulla' });

  const activeLocation = locations?.find(l => l.id === activeLocId) || (locations?.length > 0 ? locations[0] : null);

  const showAppAlert = (title, message, type = 'info', onConfirm = null, confirmText = t('global.ok'), cancelText = t('global.cancel')) => { 
    setCustomAlert({ visible: true, title, message, type, onConfirm, confirmText, cancelText }); 
  };
  const closeAppAlert = () => setCustomAlert(prev => ({ ...prev, visible: false }));

  const handleAdComplete = () => {
    setShowAdModal(false);
    addEnergy(5);
    showAppAlert(t('home.alerts.adSuccessTitle'), t('home.alerts.adSuccessMsg'), "success");
  };

  useEffect(() => {
    if (route.params?.highlightWineId) {
      setSearchResultIds([route.params.highlightWineId]);
      setActiveSearchQuery(t('caveau.localizedWine'));
      setActiveSidebar('search'); 
    }
    if (route.params?.targetLocId) {
      setActiveLocId(route.params.targetLocId);
    }
  }, [route.params]);

  useEffect(() => {
    if (locations && locations.length > 0) {
      if (!activeLocId || !locations.find(l => l.id === activeLocId)) setActiveLocId(locations[0].id);
    } else {
      setActiveLocId(null);
    }
    setIsInitializing(false);
  }, [locations]);

  useEffect(() => {
    if (!presentWines) return;
    const orphans = getUnplacedWines ? getUnplacedWines() : [];
    setUnplacedWines(orphans);
    
    if (selectedWine) {
      const target = orphans.find(w => w.id === selectedWine.id);
      if (!target || target.missingToPlace <= 0) setSelectedWine(null);
    }
  }, [locations, presentWines]);

  const executeSearch = async (specificWineId = null, specificWineName = null) => {
    if (!isPremium && userProfile?.energia < 1) { setShowOutOfEnergy(true); return; }
    const hasEnergy = await consumeEnergy(1);
    if (!hasEnergy) { setShowOutOfEnergy(true); return; }

    if (specificWineId) {
      setSearchResultIds([specificWineId]);
      setActiveSearchQuery(specificWineName);
    } else {
      const lowerQ = searchQuery.toLowerCase();
      const matches = presentWines.filter(w => w.name.toLowerCase().includes(lowerQ) || w.type.toLowerCase().includes(lowerQ) || (w.annata && w.annata.toLowerCase().includes(lowerQ))).map(w => w.id);
      setSearchResultIds(matches);
      setActiveSearchQuery(searchQuery);
      if (matches.length === 0) {
        showAppAlert(t('global.error'), t('catalog.empty.text'), "info");
      }
    }
    setSearchQuery('');
  };

  const clearSearch = () => {
    setSearchResultIds([]);
    setActiveSearchQuery('');
    setSearchQuery('');
    navigation.setParams({ highlightWineId: null, targetLocId: null });
  };

  const handleDeleteLocation = () => {
    if (!activeLocation) return;
    
    if (activeLocation.slots && activeLocation.slots.length > 0) {
      showAppAlert(
        t('caveau.alerts.notEmptyTitle'), 
        t('caveau.alerts.notEmptyMsg'), 
        "error"
      );
      return;
    }

    showAppAlert(
      t('caveau.alerts.deleteConfirmTitle'), t('caveau.alerts.deleteConfirmMsg').replace('{name}', activeLocation.name), "confirm",
      async () => {
        closeAppAlert();
        try {
          const idToDelete = activeLocation.id;
          const remaining = locations.filter(l => l.id !== idToDelete);
          setActiveLocId(remaining.length > 0 ? remaining[0].id : null);
          await deleteLocationLocal(idToDelete);
        } catch (error) { 
          showAppAlert(t('global.error'), t('caveau.alerts.updateErrorMsg'), "error"); 
        }
      }, 
      t('caveau.buttons.dismantle')
    );
  };

  const handleSlotPress = async (r, c, d = 0) => {
    if (!activeLocation) return;
    const currentSlots = activeLocation.slots || [];
    const idx = currentSlots.findIndex(s => s.row === r && s.col === c && (s.depth || 0) === d);

    if (idx !== -1) {
      const wineIdInSlot = currentSlots[idx].wineId;
      const wineObj = presentWines.find(w => w.id === wineIdInSlot);
      if (wineObj) setSlotActionModal({ visible: true, wine: wineObj, slot: { r, c, d } });
    } else {
      if (!selectedWine) return showAppAlert(t('caveau.alerts.noWineSelectedTitle'), t('caveau.alerts.noWineSelectedMsg'), "info");
      if (!isPremium && userProfile?.energia < 1) { setShowOutOfEnergy(true); return; }
      const hasEnergy = await consumeEnergy(1);
      if (!hasEnergy) { setShowOutOfEnergy(true); return; }

      try {
        const newSlot = { row: r, col: c, depth: d, wineId: selectedWine.id };
        await updateLocationLocal(activeLocation.id, { slots: [...currentSlots, newSlot] });
        const orphanTarget = unplacedWines.find(w => w.id === selectedWine.id);
        if (orphanTarget && orphanTarget.missingToPlace <= 1) setSelectedWine(null);
      } catch (error) {
        if (!isPremium) await addEnergy(1);
        showAppAlert(t('global.error'), t('caveau.alerts.placeErrorMsg'), "error");
      }
    }
  };

  const handleMoveToDeposit = async () => {
    if (!isPremium && userProfile?.energia < 1) { setSlotActionModal({...slotActionModal, visible: false}); setShowOutOfEnergy(true); return; }
    const hasEnergy = await consumeEnergy(1);
    if (!hasEnergy) { setSlotActionModal({...slotActionModal, visible: false}); setShowOutOfEnergy(true); return; }

    try {
      const newSlots = activeLocation.slots.filter(s => !(s.row === slotActionModal.slot.r && s.col === slotActionModal.slot.c && (s.depth || 0) === slotActionModal.slot.d));
      await updateLocationLocal(activeLocation.id, { slots: newSlots });
      setSlotActionModal({visible: false, wine: null, slot: null});
    } catch (e) {
      if (!isPremium) await addEnergy(1);
      showAppAlert(t('global.error'), t('caveau.alerts.updateErrorMsg'), "error");
    }
  };

  const handleConsumeBottle = async () => {
    if (!isPremium && userProfile?.energia < 1) { setSlotActionModal({...slotActionModal, visible: false}); setShowOutOfEnergy(true); return; }
    const hasEnergy = await consumeEnergy(1);
    if (!hasEnergy) { setSlotActionModal({...slotActionModal, visible: false}); setShowOutOfEnergy(true); return; }

    try {
      const newSlots = activeLocation.slots.filter(s => !(s.row === slotActionModal.slot.r && s.col === slotActionModal.slot.c && (s.depth || 0) === slotActionModal.slot.d));
      await updateLocationLocal(activeLocation.id, { slots: newSlots });
      const currentWine = slotActionModal.wine;
      const newQuantity = Math.max(0, currentWine.quantity - 1);
      const newStatus = newQuantity === 0 ? 'passato' : currentWine.status;
      await updateWineLocal(currentWine.id, { quantity: newQuantity, status: newStatus });
      setSlotActionModal({visible: false, wine: null, slot: null});
      showAppAlert(t('caveau.alerts.consumeSuccessTitle'), t('caveau.alerts.consumeSuccessMsg').replace('{name}', currentWine.name), "success");
    } catch (e) {
      if (!isPremium) await addEnergy(1);
      showAppAlert(t('global.error'), t('caveau.alerts.updateErrorMsg'), "error");
    }
  };

  const handleCreateCaveau = async () => {
    if (!newLocName.trim()) return showAppAlert(t('global.error'), t('addWine.alerts.nameRequiredMsg'), "error");
    await addLocationLocal({ 
      name: newLocName, 
      type: newLocType, 
      temperature: Number(newLocTemp), 
      rows: newRows, 
      cols: newCols, 
      depth: newLocType === 'vertical' ? 2 : 1, 
      slots: [] 
    });
    setShowCreateModal(false); setNewLocName(''); setNewLocType('horizontal'); setNewLocTemp(14); setNewRows(3); setNewCols(4);
  };

  const handleOpenEditModal = () => {
    if (!activeLocation) return;
    setEditLocName(activeLocation.name);
    setEditLocType(activeLocation.type);
    setEditLocTemp(activeLocation.temperature ?? 14);
    setShowEditModal(true);
  };

  const handleUpdateCaveau = async () => {
    if (!editLocName.trim()) return showAppAlert(t('global.error'), t('addWine.alerts.nameRequiredMsg'), "error");
    try {
      await updateLocationLocal(activeLocation.id, {
        name: editLocName,
        type: editLocType,
        temperature: Number(editLocTemp)
      });
      setShowEditModal(false);
      showAppAlert(t('global.success'), t('profile.alerts.saveSuccessMsg'), "success");
    } catch (error) {
      showAppAlert(t('global.error'), t('caveau.alerts.updateDetailsErrorMsg'), "error");
    }
  };

  const handleExpand = async (dimension, amount) => {
    if (!activeLocation) return;
    const currentRows = activeLocation.rows || 1;
    const currentCols = activeLocation.cols || 1;
    const currentDepth = activeLocation.depth || 1;

    if (amount < 0) {
      let hasBottles = false;
      const slots = activeLocation.slots || [];
      if (dimension === 'row') { if (currentRows <= 1) return; hasBottles = slots.some(s => s.row === currentRows - 1); } 
      else if (dimension === 'col') { if (currentCols <= 1) return; hasBottles = slots.some(s => s.col === currentCols - 1); } 
      else if (dimension === 'depth') { if (currentDepth <= 1) return; hasBottles = slots.some(s => (s.depth || 0) === currentDepth - 1); }

      if (hasBottles) {
        showAppAlert(t('caveau.alerts.notEmptyTitle'), t('caveau.alerts.notEmptyMsg'), "error");
        return; 
      }
    }

    try {
      const updates = {};
      if (dimension === 'row') updates.rows = Math.max(1, currentRows + amount);
      if (dimension === 'col') updates.cols = Math.max(1, currentCols + amount);
      if (dimension === 'depth') updates.depth = Math.max(1, currentDepth + amount);
      await updateLocationLocal(activeLocation.id, updates);
    } catch (e) { showAppAlert(t('global.error'), t('caveau.alerts.updateDetailsErrorMsg'), "error"); }
  };

  const handleAskPlacementAi = async () => {
    if (!selectedWine) return showAppAlert(t('global.error'), t('caveau.alerts.noWineSelectedMsg'), "info");
    if (!locations || locations.length === 0) return showAppAlert(t('global.error'), t('caveau.alerts.placeErrorMsg'), "info");

    if (!isPremium && userProfile?.energia < 1) { setShowOutOfEnergy(true); return; }
    const hasEnergy = await consumeEnergy(1);
    if (!hasEnergy) { setShowOutOfEnergy(true); return; }

    setIsAiRecommending(true);
    try {
      const descriptionsOfAllCellars = locations.map(l => 
        `- Cantina: "${l.name}", Tipo: ${l.type === 'vertical' ? 'In Piedi' : 'Sdraiata'}, Temperatura: ${l.temperature ?? 14}°C, Capienza: ${l.rows} piani x ${l.cols} colonne.`
      ).join('\n');

      const langNames = { it: "Italiano", en: "Inglese (English)", fr: "Francese (Français)" };
      const targetLanguageName = langNames[userProfile?.language || 'it'] || "Italiano";

      const promptText = `Agisci come un Master Sommelier ed Esperto di Conservazione Enologica. È stata selezionata la bottiglia: "${selectedWine.name}" (Tipologia: ${selectedWine.type}).
       L'utente dispone complessivamente di queste ${locations.length} strutture cantina:\n${descriptionsOfAllCellars}\n
       La cantina attualmente aperta a schermo è "${activeLocation?.name || 'Nessuna'}".
       
       Effettua un'analisi comparativa tra TUTTE le cantine a disposizione. Determina qual è la cantina strutturalmente e termicamente ideale per questa specifica referenza vinicola e descrivi in quale piano/slot specifico convenga posizionarla strategicamente (considerando che il calore sale e il freddo scende).
       
       REGOLA DI TONO FONDAMENTALE: Usa un tono rigorosamente NEUTRO, impersonale e diretto. Rispondi interamente in lingua ${targetLanguageName}. Non dare assolutamente del "Lei" o del "Voi". Non usare espressioni formali come "la Sua cantina" o "Le suggerisco di", bensì formule descrittive come "si consiglia di", "la scelta ottimale ricade su", "posizionare nel vano...". Sii conciso ed elegante (massimo 3 righe complessive).`;

      const response = await callGemini(promptText, false);
      showAppAlert(t('caveau.alerts.aiPlacementTitle'), response, "success");
    } catch (error) {
      if (!isPremium) await addEnergy(1); 
      showAppAlert(t('global.error'), t('caveau.alerts.aiConnectionErrorMsg'), "error");
    } finally {
      setIsAiRecommending(false);
    }
  };

  return (
    <KeyboardAvoidingView behavior={Platform.OS === "ios" ? "padding" : undefined} style={styles.container}>
      <View style={styles.topRedBanner}><EnergyHeader /></View>

      <View style={styles.pageHeaderCushion}>
        <View style={styles.headerLeft}>
          <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backButton}><ArrowLeft color="#0f172a" size={24} /></TouchableOpacity>
          <View style={{ flex: 1 }}>
            <Text style={styles.headerTitle}>{t('caveau.title')}</Text>
            {locations.length > 0 && (
              <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.locSelector}>
                {locations.map(loc => {
                  const hasMatch = searchResultIds.length > 0 && loc.slots?.some(s => searchResultIds.includes(s.wineId));
                  return (
                    <TouchableOpacity key={loc.id} style={[styles.locBadge, activeLocId === loc.id && styles.locBadgeActive]} onPress={() => setActiveLocId(loc.id)}>
                      <Text style={[styles.locBadgeText, activeLocId === loc.id && styles.locBadgeTextActive]}>{loc.name} ({loc.temperature ?? 14}°C)</Text>
                      {hasMatch && <View style={styles.matchDot} />}
                    </TouchableOpacity>
                  );
                })}
              </ScrollView>
            )}
          </View>
        </View>
        <View style={styles.headerActions}>
          {locations.length > 0 && (
            <TouchableOpacity style={[styles.deleteLocationBtn, { backgroundColor: '#f1f5f9', width: 'auto', paddingHorizontal: 12 }]} onPress={handleOpenEditModal}>
              <Text style={{ color: '#334155', fontSize: 13, fontWeight: 'bold' }}>{t('caveau.buttons.edit')}</Text>
            </TouchableOpacity>
          )}
          {locations.length > 0 && (<TouchableOpacity style={styles.deleteLocationBtn} onPress={handleDeleteLocation}><Trash2 color="#ef4444" size={20} /></TouchableOpacity>)}
          <TouchableOpacity style={styles.addLocationBtn} onPress={() => setShowCreateModal(true)}><Plus color="#e11d48" size={24} /></TouchableOpacity>
        </View>
      </View>

      {selectedWine && !isDesktop && (
        <View style={styles.activeStateBannerColloca}>
          <View style={{ flex: 1 }}>
            <Text style={styles.activeStateTextColloca}>{t('caveau.activeSpaceFor')} <Text style={{fontWeight: 'bold'}}>{selectedWine.name}</Text></Text>
            <TouchableOpacity style={styles.aiPlacementRowButton} onPress={handleAskPlacementAi} disabled={isAiRecommending}>
              <Sparkles size={14} color="#16a34a" />
              {isAiRecommending ? <ActivityIndicator size="small" color="#16a34a" style={{ marginLeft: 6 }} /> : <Text style={styles.aiPlacementLinkText}>{t('caveau.aiRecommendation')}</Text>}
            </TouchableOpacity>
          </View>
          <TouchableOpacity onPress={() => setSelectedWine(null)}><Text style={styles.cancelStateText}>{t('caveau.deselect')}</Text></TouchableOpacity>
        </View>
      )}
      
      {searchResultIds.length > 0 && (
        <View style={styles.activeStateBannerSearch}>
          <Text style={styles.activeStateTextSearch}>🔍 {t('caveau.searchResultsFor')} "{activeSearchQuery}" ({searchResultIds.length})</Text>
          <TouchableOpacity onPress={clearSearch}><Text style={styles.cancelSearchText}>{t('caveau.clearSearch')}</Text></TouchableOpacity>
        </View>
      )}

      <View style={[styles.mainBodyWrapper, isDesktop && styles.mainBodyWrapperDesktop]}>
        <View style={styles.canvasSection}>
          <View style={styles.canvasContainerFullScreen}>
            {active && (
              <View style={styles.loadingOverlay}>
                <ActivityIndicator size="large" color="#e11d48" style={{marginBottom: 16}} />
                <Text style={styles.loadingText}>{t('caveau.loadingCellar')}</Text>
                <Text style={styles.loadingSubText}>{t('caveau.loadingCellarSub')}</Text>
              </View>
            )}

            {activeLocation ? (
              <Canvas camera={{ position: [0, 0, Math.max(activeLocation.cols, activeLocation.rows) * 2.0], fov: 45 }}>
                <Suspense fallback={null}>
                  <Environment preset="city" />
                  <ambientLight intensity={1.4} color="#ffffff" />
                  <directionalLight position={[10, 15, 10]} intensity={1.8} color="#ffffff" />
                  <directionalLight position={[-10, 10, -5]} intensity={0.8} color="#f8fafc" />
                  <pointLight position={[0, 0, 10]} intensity={1.5} color="#ffffff" />
                </Suspense>
                <Shelf3D activeLocation={activeLocation} presentWines={presentWines} onSlotPress={handleSlotPress} searchResultIds={searchResultIds} />
                <OrbitControls enablePan={true} maxPolarAngle={Math.PI / 2 + 0.2} minPolarAngle={Math.PI / 4} />
              </Canvas>
            ) : (
              <View style={styles.emptyState}>
                <Text style={styles.emptyText}>{t('caveau.emptyCellars')}</Text>
                <TouchableOpacity style={styles.primaryBtn} onPress={() => setShowCreateModal(true)}><Text style={styles.primaryBtnText}>{t('caveau.buildFirst')}</Text></TouchableOpacity>
              </View>
            )}

            {activeLocation && (
              <View style={styles.floatingExpandControls}>
                <View style={styles.expGroup}><TouchableOpacity style={styles.expBtnSide} onPress={() => handleExpand('row', -1)}><Minus size={14} color="#64748b"/></TouchableOpacity><Text style={styles.expGroupText}>{t('caveau.expand.row')}</Text><TouchableOpacity style={styles.expBtnSide} onPress={() => handleExpand('row', 1)}><Plus size={14} color="#64748b"/></TouchableOpacity></View>
                <View style={styles.expGroup}><TouchableOpacity style={styles.expBtnSide} onPress={() => handleExpand('col', -1)}><Minus size={14} color="#64748b"/></TouchableOpacity><Text style={styles.expGroupText}>{t('caveau.expand.col')}</Text><TouchableOpacity style={styles.expBtnSide} onPress={() => handleExpand('col', 1)}><Plus size={14} color="#64748b"/></TouchableOpacity></View>
                {activeLocation.type === 'vertical' && (<View style={styles.expGroup}><TouchableOpacity style={styles.expBtnSide} onPress={() => handleExpand('depth', -1)}><Minus size={14} color="#64748b"/></TouchableOpacity><Text style={styles.expGroupText}>{t('caveau.expand.depth')}</Text><TouchableOpacity style={styles.expBtnSide} onPress={() => handleExpand('depth', 1)}><Plus size={14} color="#64748b"/></TouchableOpacity></View>)}
              </View>
            )}
          </View>
        </View>

        <View style={isDesktop ? styles.sidebarContainerDesktop : styles.sidebarContainerMobile}>
          {activeSidebar === 'none' && (
            <View style={isDesktop ? styles.sidebarClosed : styles.sidebarClosedMobile}>
              <TouchableOpacity style={styles.sidebarIconBtn} onPress={() => setActiveSidebar('deposit')}>
                <Box color="#0f172a" size={24} />
                {unplacedWines.length > 0 && <View style={styles.sidebarIconBadge}><Text style={styles.sidebarIconBadgeText}>{unplacedWines.reduce((a,w)=>a+w.missingToPlace,0)}</Text></View>}
              </TouchableOpacity>
              <TouchableOpacity style={styles.sidebarIconBtn} onPress={() => setActiveSidebar('search')}><Search color="#0f172a" size={24} />{searchResultIds.length > 0 && <View style={[styles.sidebarIconBadge, {backgroundColor: '#10b981'}]} />}</TouchableOpacity>
            </View>
          )}

          {activeSidebar === 'deposit' && (
            <View style={[styles.sidebarExpanded, !isDesktop && styles.sidebarExpandedMobile]}>
              <View style={styles.sidebarHeader}><Text style={styles.sidebarTitle}>{t('caveau.modals.inventoryToPlace')}</Text><TouchableOpacity onPress={() => setActiveSidebar('none')} style={styles.closeSidebarBtn}><X color="#64748b" size={20} /></TouchableOpacity></View>
              
              {selectedWine && (
                <View style={styles.activeStateBannerColloca}>
                  <Text style={styles.activeStateTextColloca}>{t('caveau.activeSpaceFor')} <Text style={{fontWeight: 'bold'}}>{selectedWine.name}</Text></Text>
                  <TouchableOpacity style={styles.aiPlacementRowButton} onPress={handleAskPlacementAi} disabled={isAiRecommending}>
                    <Sparkles size={13} color="#16a34a" />
                    {isAiRecommending ? <ActivityIndicator size="small" color="#16a34a" style={{ marginLeft: 4 }} /> : <Text style={styles.aiPlacementLinkText}>{t('caveau.aiRecommendation')}</Text>}
                  </TouchableOpacity>
                  <TouchableOpacity style={{ marginTop: 8 }} onPress={() => setSelectedWine(null)}><Text style={styles.cancelStateText}>{t('caveau.deselect')}</Text></TouchableOpacity>
                </View>
              )}

              {unplacedWines.length === 0 ? (
                <Text style={styles.depositEmptyText}>{t('caveau.modals.inventoryEmpty')}</Text>
              ) : (
                <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={{gap: 12}}>
                  {unplacedWines.map(w => (
                    <TouchableOpacity key={w.id} style={[styles.wineListCard, selectedWine?.id === w.id && styles.wineListCardActive]} onPress={() => setSelectedWine(selectedWine?.id === w.id ? null : w)}>
                      <View style={styles.wineListIconBg}><Wine color={selectedWine?.id === w.id ? "#e11d48" : "#64748b"} size={20} /></View>
                      <View style={{flex: 1}}><Text style={[styles.wineListName, selectedWine?.id === w.id && {color: '#e11d48'}]} numberOfLines={2}>{w.name}</Text></View>
                      <View style={styles.qtyBadgeList}><Text style={styles.qtyText}>{w.missingToPlace}</Text></View>
                    </TouchableOpacity>
                  ))}
                </ScrollView>
              )}
            </View>
          )}

          {activeSidebar === 'search' && (
            <View style={[styles.sidebarExpanded, !isDesktop && styles.sidebarExpandedMobile]}>
              <View style={styles.sidebarHeader}><Text style={styles.sidebarTitle}>{t('caveau.modals.searchTitle')}</Text><TouchableOpacity onPress={() => setActiveSidebar('none')} style={styles.closeSidebarBtn}><X color="#64748b" size={20} /></TouchableOpacity></View>
              {searchResultIds.length > 0 && (
                <View style={styles.activeStateBannerSearch}><Text style={styles.activeStateTextSearch}>📍 {t('caveau.searchResultsFor')} "{activeSearchQuery}" ({searchResultIds.length})</Text><TouchableOpacity onPress={clearSearch}><Text style={styles.cancelSearchText}>{t('caveau.clearSearch')}</Text></TouchableOpacity></View>
              )}
              <View style={styles.searchBoxInputContainer}><Search color="#94a3b8" size={20} style={{marginRight: 8}} /><TextInput style={styles.sidebarSearchInput} placeholder={t('caveau.modals.searchPlaceholder')} placeholderTextColor="#94a3b8" value={searchQuery} onChangeText={setSearchQuery} onSubmitEditing={() => executeSearch(null, null)} /></View>
              {searchSuggestions.length > 0 && (
                <ScrollView style={styles.suggestionsContainer} keyboardShouldPersistTaps="handled">
                  <Text style={styles.suggestionsLabel}>{t('caveau.modals.searchSuggestionsLabel')}</Text>
                  {searchSuggestions.map(w => (
                    <TouchableOpacity key={w.id} style={styles.suggestionRow} onPress={() => executeSearch(w.id, w.name)}><Wine color="#475569" size={16} style={{marginRight: 8}} /><Text style={styles.suggestionText} numberOfLines={1}>{w.name}</Text><ChevronRight color="#cbd5e1" size={16} /></TouchableOpacity>
                  ))}
                </ScrollView>
              )}
            </View>
          )}
        </View>
      </View>

      <Modal transparent={true} visible={slotActionModal.visible} animationType="fade">
        <View style={styles.modalOverlay}>
          <View style={styles.modalBox}>
            <Text style={[styles.modalTitle, { marginBottom: 16, marginLeft: 0 }]}>{t('caveau.modals.selectedBottle')}</Text>
            <View style={styles.wineHighlightBox}><Wine color="#e11d48" size={32} /><Text style={styles.wineHighlightText}>{slotActionModal.wine?.name}</Text></View>
            <Text style={styles.modalMessage}>{t('caveau.modals.slotActionMsg')}</Text>
            <View style={{ gap: 12, marginTop: 10 }}>
              <TouchableOpacity style={styles.saveBtn} onPress={handleConsumeBottle}><Text style={styles.saveBtnText}>{t('caveau.modals.consumeAction')}</Text></TouchableOpacity>
              <TouchableOpacity style={[styles.cancelBtn, {borderWidth: 1, borderColor: '#cbd5e1', backgroundColor: '#fff', paddingVertical: 14}]} onPress={handleMoveToDeposit}><Text style={[styles.cancelBtnText, {color: '#0f172a'}]}>{t('caveau.modals.returnToDeposit')}</Text></TouchableOpacity>
              <TouchableOpacity style={[styles.cancelBtn, {paddingVertical: 14}]} onPress={() => setSlotActionModal({visible: false, wine: null, slot: null})}><Text style={styles.cancelBtnText}>{t('global.cancel')}</Text></TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      <Modal visible={showCreateModal} animationType="fade" transparent>
        <View style={styles.modalOverlay}>
          <View style={styles.modalBox}>
            <Text style={styles.modalTitle}>{t('caveau.modals.createCellarTitle')}</Text>
            <Text style={styles.label}>{t('caveau.modals.cellarNameLabel')}</Text>
            <TextInput style={styles.input} placeholder={t('caveau.modals.cellarNamePlaceholder')} placeholderTextColor="#94a3b8" value={newLocName} onChangeText={setNewLocName} />
            
            <Text style={styles.label}>{t('caveau.modals.cellarTempLabel')}</Text>
            <View style={[styles.stepper, { marginBottom: 20, justifyContent: 'space-between', paddingHorizontal: 10 }]}>
              <TouchableOpacity onPress={() => setNewLocTemp(Math.max(4, newLocTemp - 1))} style={styles.stepBtn}><Minus size={16} color="#0f172a" /></TouchableOpacity>
              <Text style={[styles.stepValue, { width: 'auto' }]}>{newLocTemp} °C</Text>
              <TouchableOpacity onPress={() => setNewLocTemp(Math.min(38, newLocTemp + 1))} style={styles.stepBtn}><Plus size={16} color="#0f172a" /></TouchableOpacity>
            </View>

            <Text style={styles.label}>{t('caveau.modals.cellarArrangementLabel')}</Text>
            <View style={styles.typeToggleRow}>
              <TouchableOpacity style={[styles.typeToggleBtn, newLocType === 'horizontal' && styles.typeToggleActive]} onPress={() => setNewLocType('horizontal')}><Rows3 color={newLocType === 'horizontal' ? '#e11d48' : '#94a3b8'} size={24} /><Text style={[styles.typeToggleText, newLocType === 'horizontal' && {color: '#e11d48'}]}>{t('caveau.modals.arrangements.horizontal')}</Text></TouchableOpacity>
              <TouchableOpacity style={[styles.typeToggleBtn, newLocType === 'vertical' && styles.typeToggleActive]} onPress={() => setNewLocType('vertical')}><AlignVerticalSpaceAround color={newLocType === 'vertical' ? '#e11d48' : '#94a3b8'} size={24} /><Text style={[styles.typeToggleText, newLocType === 'vertical' && {color: '#e11d48'}]}>{t('caveau.modals.arrangements.vertical')}</Text></TouchableOpacity>
            </View>
            <View style={styles.dimensionRow}>
              <View style={styles.dimensionControl}><Text style={styles.label}>{t('caveau.modals.dimensions.rows')}</Text><View style={styles.stepper}><TouchableOpacity onPress={() => setNewRows(Math.max(1, newRows - 1))} style={styles.stepBtn}><Minus size={16} color="#0f172a" /></TouchableOpacity><Text style={styles.stepValue}>{newRows}</Text><TouchableOpacity onPress={() => setNewRows(Math.min(10, newRows + 1))} style={styles.stepBtn}><Plus size={16} color="#0f172a" /></TouchableOpacity></View></View>
              <View style={styles.dimensionControl}><Text style={styles.label}>{t('caveau.modals.dimensions.cols')}</Text><View style={styles.stepper}><TouchableOpacity onPress={() => setNewCols(Math.max(1, newCols - 1))} style={styles.stepBtn}><Minus size={16} color="#0f172a" /></TouchableOpacity><Text style={styles.stepValue}>{newCols}</Text><TouchableOpacity onPress={() => setNewCols(Math.min(10, newCols + 1))} style={styles.stepBtn}><Plus size={16} color="#0f172a" /></TouchableOpacity></View></View>
            </View>
            <View style={styles.modalButtonsGroup}>
              {locations.length > 0 && <TouchableOpacity style={styles.cancelBtn} onPress={() => setShowCreateModal(false)}><Text style={styles.cancelBtnText}>{t('global.cancel')}</Text></TouchableOpacity>}
              <TouchableOpacity style={styles.saveBtn} onPress={handleCreateCaveau}><Text style={styles.saveBtnText}>{t('caveau.modals.createBtn')}</Text></TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      <Modal visible={showEditModal} animationType="fade" transparent>
        <View style={styles.modalOverlay}>
          <View style={styles.modalBox}>
            <Text style={styles.modalTitle}>{t('caveau.modals.editCellarTitle')}</Text>
            <Text style={styles.label}>{t('caveau.modals.cellarIdentifierLabel')}</Text>
            <TextInput style={styles.input} placeholderTextColor="#94a3b8" value={editLocName} onChangeText={setEditLocName} />
            
            <Text style={styles.label}>{t('caveau.modals.cellarTempLabel')}</Text>
            <View style={[styles.stepper, { marginBottom: 20, justifyContent: 'space-between', paddingHorizontal: 10 }]}>
              <TouchableOpacity onPress={() => setEditLocTemp(Math.max(4, editLocTemp - 1))} style={styles.stepBtn}><Minus size={16} color="#0f172a" /></TouchableOpacity>
              <Text style={[styles.stepValue, { width: 'auto' }]}>{editLocTemp} °C</Text>
              <TouchableOpacity onPress={() => setEditLocTemp(Math.min(38, editLocTemp + 1))} style={styles.stepBtn}><Plus size={16} color="#0f172a" /></TouchableOpacity>
            </View>

            <Text style={styles.label}>{t('caveau.modals.bottleArrangementLabel')}</Text>
            <View style={styles.typeToggleRow}>
              <TouchableOpacity style={[styles.typeToggleBtn, editLocType === 'horizontal' && styles.typeToggleActive]} onPress={() => setEditLocType('horizontal')}><Rows3 color={editLocType === 'horizontal' ? '#e11d48' : '#94a3b8'} size={24} /><Text style={[styles.typeToggleText, editLocType === 'horizontal' && {color: '#e11d48'}]}>{t('caveau.modals.arrangements.horizontal')}</Text></TouchableOpacity>
              <TouchableOpacity style={[styles.typeToggleBtn, editLocType === 'vertical' && styles.typeToggleActive]} onPress={() => setEditLocType('vertical')}><AlignVerticalSpaceAround color={editLocType === 'vertical' ? '#e11d48' : '#94a3b8'} size={24} /><Text style={[styles.typeToggleText, editLocType === 'vertical' && {color: '#e11d48'}]}>{t('caveau.modals.arrangements.vertical')}</Text></TouchableOpacity>
            </View>

            <Text style={[styles.label, { color: '#94a3b8', fontStyle: 'italic', textTransform: 'none' }]}>{t('caveau.modals.editHint')}</Text>

            <View style={[styles.modalButtonsGroup, { marginTop: 16 }]}>
              <TouchableOpacity style={styles.cancelBtn} onPress={() => setShowEditModal(false)}><Text style={styles.cancelBtnText}>{t('caveau.modals.closeBtn')}</Text></TouchableOpacity>
              <TouchableOpacity style={styles.saveBtn} onPress={handleUpdateCaveau}><Text style={styles.saveBtnText}>{t('details.modals.saveChanges')}</Text></TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      <Modal transparent={true} visible={customAlert.visible} animationType="fade">
        <View style={styles.modalOverlay}>
          <View style={styles.modalBox}>
            <View style={styles.modalHeader}>
              {customAlert.type === 'error' ? <AlertCircle color="#ef4444" size={28} /> : customAlert.type === 'confirm' ? <Trash2 color="#ef4444" size={28} /> : customAlert.type === 'success' ? <CheckCircle2 color="#10b981" size={28} /> : <Info color="#3b82f6" size={28} />}
              <Text style={styles.modalTitle}>{customAlert.title}</Text>
            </View>
            <Text style={styles.modalMessage}>{customAlert.message}</Text>
            <View style={styles.modalButtonsGroup}>
              {customAlert.type === 'confirm' ? <TouchableOpacity style={styles.cancelBtn} onPress={closeAppAlert}><Text style={styles.cancelBtnText}>{customAlert.cancelText}</Text></TouchableOpacity> : null}
              <TouchableOpacity style={[styles.saveBtn, customAlert.type === 'error' ? { backgroundColor: '#e11d48' } : customAlert.type === 'success' ? { backgroundColor: '#10b981' } : (customAlert.type === 'confirm' ? { backgroundColor: '#e11d48' } : { backgroundColor: '#0f172a' })]} onPress={() => { if (customAlert.onConfirm) customAlert.onConfirm(); else closeAppAlert(); }}>
                <Text style={styles.saveBtnText}>{customAlert.confirmText}</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      <OutOfEnergyModal visible={showOutOfEnergy} onClose={() => setShowOutOfEnergy(false)} onWatchAd={() => { setShowOutOfEnergy(false); setShowAdModal(true); }} onSubscribe={() => { setShowOutOfEnergy(false); navigation.navigate('Paywall'); }} />
      <RewardedAdModal visible={showAdModal} onClose={() => setShowAdModal(false)} onReward={handleAdComplete} />
    </KeyboardAvoidingView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#f8fafc' }, 
  centered: { flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: '#f8fafc' },
  topRedBanner: { backgroundColor: '#e11d48', paddingBottom: 16, borderBottomLeftRadius: 24, borderBottomRightRadius: 24, elevation: 5, zIndex: 50 },
  pageHeaderCushion: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', backgroundColor: 'white', marginHorizontal: 16, marginTop: 16, marginBottom: 12, padding: 16, borderRadius: 20, elevation: 3, zIndex: 10 },
  headerLeft: { flexDirection: 'row', alignItems: 'center', flex: 1 },
  backButton: { marginRight: 10, padding: 6, backgroundColor: '#f1f5f9', borderRadius: 10 },
  headerTitle: { fontSize: 20, fontWeight: 'bold', color: '#0f172a', marginBottom: 4 },
  locSelector: { flexDirection: 'row', marginTop: 4 },
  locBadge: { paddingHorizontal: 12, paddingVertical: 6, borderRadius: 10, backgroundColor: '#f1f5f9', marginRight: 8, borderWidth: 1, borderColor: '#e2e8f0', flexDirection: 'row', alignItems: 'center' },
  locBadgeActive: { backgroundColor: '#e11d48', borderColor: '#e11d48' },
  locBadgeText: { color: '#64748b', fontSize: 13, fontWeight: 'bold' },
  locBadgeTextActive: { color: '#fff' },
  locTempIconInline: { fontSize: 12, marginLeft: 3 },
  matchDot: { width: 8, height: 8, borderRadius: 4, backgroundColor: '#ef4444', marginLeft: 6, borderWidth: 1, borderColor: 'white' },
  headerActions: { flexDirection: 'row', alignItems: 'center', gap: 10 },
  deleteLocationBtn: { padding: 8, backgroundColor: '#fef2f2', borderRadius: 10, justifyContent: 'center', alignItems: 'center' },
  addLocationBtn: { padding: 8, backgroundColor: '#fff1f2', borderRadius: 10 },
  mainBodyWrapper: { flex: 1, flexDirection: 'column', position: 'relative' },
  mainBodyWrapperDesktop: { flexDirection: 'row', flex: 1, marginHorizontal: 16, marginBottom: 16, gap: 12 },
  canvasSection: { flex: 1, flexDirection: 'column' }, 
  canvasContainerFullScreen: { flex: 1, backgroundColor: '#f8fafc', borderRadius: 24, overflow: 'hidden', borderWidth: 1, borderColor: '#e2e8f0', position: 'relative' }, 
  loadingOverlay: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(255,255,255,0.95)', zIndex: 100, justifyContent: 'center', alignItems: 'center' },
  loadingText: { fontSize: 18, fontWeight: 'bold', color: '#0f172a', marginBottom: 8 },
  loadingSubText: { fontSize: 14, color: '#64748b', textAlign: 'center', paddingHorizontal: 40 },
  floatingExpandControls: { position: 'absolute', top: 16, left: 0, right: 0, flexDirection: 'row', justifyContent: 'center', flexWrap: 'wrap', gap: 8, pointerEvents: 'box-none' },
  expGroup: { flexDirection: 'row', alignItems: 'center', backgroundColor: 'rgba(255,255,255,0.95)', borderRadius: 20, borderWidth: 1, borderColor: '#e2e8f0', overflow: 'hidden', pointerEvents: 'auto' },
  expBtnSide: { paddingHorizontal: 12, paddingVertical: 8, backgroundColor: '#f8fafc' },
  expGroupText: { color: '#0f172a', fontSize: 12, fontWeight: 'bold', paddingHorizontal: 8 },
  emptyState: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  emptyText: { color: '#64748b', fontSize: 16, marginBottom: 20 },
  primaryBtn: { backgroundColor: '#e11d48', paddingHorizontal: 24, paddingVertical: 14, borderRadius: 12 },
  primaryBtnText: { color: '#fff', fontWeight: 'bold' },
  sidebarContainerDesktop: { height: '100%', flexDirection: 'row', alignItems: 'flex-start' },
  sidebarContainerMobile: { position: 'absolute', bottom: 16, left: 16, right: 16, alignItems: 'center', zIndex: 100, pointerEvents: 'box-none' },
  sidebarClosed: { backgroundColor: 'white', padding: 8, borderRadius: 20, gap: 12, elevation: 3, borderWidth: 1, borderColor: '#e2e8f0' },
  sidebarClosedMobile: { flexDirection: 'row', backgroundColor: 'white', padding: 12, borderRadius: 24, gap: 24, elevation: 5, borderWidth: 1, borderColor: '#e2e8f0', shadowColor: '#000', shadowOffset: {width: 0, height: 4}, shadowOpacity: 0.15, shadowRadius: 10, pointerEvents: 'auto' },
  sidebarIconBtn: { padding: 12, backgroundColor: '#f8fafc', borderRadius: 12, position: 'relative' },
  sidebarIconBadge: { position: 'absolute', top: -5, right: -5, backgroundColor: '#e11d48', minWidth: 20, height: 20, borderRadius: 10, justifyContent: 'center', alignItems: 'center', borderWidth: 2, borderColor: 'white' },
  sidebarIconBadgeText: { color: 'white', fontSize: 10, fontWeight: 'bold' },
  sidebarExpanded: { width: 320, backgroundColor: 'white', height: '100%', borderRadius: 24, padding: 20, elevation: 5, borderWidth: 1, borderColor: '#e2e8f0', display: 'flex', flexDirection: 'column' },
  sidebarExpandedMobile: { width: '100%', height: 400, maxHeight: '80%', borderRadius: 24, padding: 20, backgroundColor: 'white', elevation: 10, shadowColor: '#000', shadowOffset: {width: 0, height: -4}, shadowOpacity: 0.15, shadowRadius: 10, pointerEvents: 'auto' },
  sidebarHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 },
  sidebarTitle: { fontSize: 18, fontWeight: 'bold', color: '#0f172a' },
  closeSidebarBtn: { padding: 6, backgroundColor: '#f1f5f9', borderRadius: 12 },
  activeStateBannerColloca: { marginBottom: 16, backgroundColor: '#f0fdf4', padding: 12, borderRadius: 12, borderWidth: 1, borderColor: '#bbf7d0' },
  activeStateTextColloca: { color: '#166534', fontSize: 13, marginBottom: 4 },
  aiPlacementRowButton: { flexDirection: 'row', alignItems: 'center', marginTop: 6, backgroundColor: '#fff', padding: 8, borderRadius: 8, borderWidth: 1, borderColor: '#bbf7d0' },
  aiPlacementLinkText: { color: '#16a34a', fontSize: 12, fontWeight: '700', textDecorationLine: 'underline', marginLeft: 4 },
  cancelStateText: { color: '#16a34a', fontWeight: 'bold', textDecorationLine: 'underline', fontSize: 12 },
  activeStateBannerSearch: { marginBottom: 16, backgroundColor: '#fff1f2', padding: 12, borderRadius: 12, borderWidth: 1, borderColor: '#ffe4e6', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  activeStateTextSearch: { color: '#e11d48', fontSize: 13, fontWeight: '600', flex: 1 },
  cancelSearchText: { color: '#be123c', fontWeight: 'bold', fontSize: 12 },
  depositEmptyText: { color: '#64748b', fontStyle: 'italic', textAlign: 'center', marginTop: 20 },
  wineListCard: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#f8fafc', padding: 10, borderRadius: 12, borderWidth: 1, borderColor: '#e2e8f0' },
  wineListCardActive: { backgroundColor: '#fff1f2', borderColor: '#e11d48' },
  wineListIconBg: { padding: 8, borderRadius: 10, marginRight: 10 },
  wineListName: { fontSize: 13, fontWeight: 'bold', color: '#0f172a' },
  qtyBadgeList: { backgroundColor: '#0f172a', minWidth: 24, height: 24, borderRadius: 12, justifyContent: 'center', alignItems: 'center', marginLeft: 10 },
  searchBoxInputContainer: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#f8fafc', borderWidth: 1, borderColor: '#cbd5e1', borderRadius: 12, paddingHorizontal: 12 },
  sidebarSearchInput: { flex: 1, paddingVertical: 12, fontSize: 14, color: '#0f172a', outlineStyle: 'none' },
  suggestionsContainer: { marginTop: 12, borderTopWidth: 1, borderTopColor: '#f1f5f9', paddingTop: 12 },
  suggestionsLabel: { fontSize: 12, color: '#94a3b8', fontWeight: 'bold', textTransform: 'uppercase', marginBottom: 8 },
  suggestionRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: '#f8fafc' },
  suggestionText: { flex: 1, fontSize: 14, color: '#334155', fontWeight: '500' },
  qtyText: { color: '#fff', fontSize: 11, fontWeight: 'bold' },
  modalOverlay: { flex: 1, backgroundColor: 'rgba(15, 23, 42, 0.6)', justifyContent: 'center', alignItems: 'center', padding: 20 },
  modalBox: { backgroundColor: '#fff', borderRadius: 24, padding: 24, width: '100%', maxWidth: 400, elevation: 10 },
  modalHeader: { flexDirection: 'row', alignItems: 'center', marginBottom: 16 },
  modalTitle: { fontSize: 20, fontWeight: 'bold', color: '#0f172a', flex: 1 },
  wineHighlightBox: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#fff1f2', borderWidth: 1, borderColor: '#ffe4e6', borderRadius: 16, padding: 16, marginBottom: 20, gap: 12 },
  wineHighlightText: { flex: 1, fontSize: 18, fontWeight: 'bold', color: '#9f1239', lineHeight: 24 },
  modalMessage: { fontSize: 16, color: '#475569', lineHeight: 24, marginBottom: 24 },
  label: { fontSize: 13, fontWeight: 'bold', color: '#64748b', marginBottom: 8, textTransform: 'uppercase' },
  input: { backgroundColor: '#f8fafc', padding: 14, borderRadius: 12, fontSize: 16, borderWidth: 1, borderColor: '#e2e8f0', color: '#0f172a', marginBottom: 20 },
  typeToggleRow: { flexDirection: 'row', gap: 12, marginBottom: 25 },
  typeToggleBtn: { flex: 1, backgroundColor: '#f8fafc', borderWidth: 1, borderColor: '#e2e8f0', borderRadius: 12, padding: 16, alignItems: 'center', cursor: 'pointer' },
  typeToggleActive: { borderColor: '#e11d48', backgroundColor: '#fff1f2', borderWidth: 2 },
  typeToggleText: { color: '#64748b', fontWeight: 'bold', marginTop: 8 },
  dimensionRow: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 35 },
  dimensionControl: { flex: 1, alignItems: 'center' },
  stepper: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#f8fafc', borderRadius: 12, borderWidth: 1, borderColor: '#e2e8f0', padding: 5 },
  stepBtn: { padding: 10, backgroundColor: '#e2e8f0', borderRadius: 8, cursor: 'pointer' },
  stepValue: { fontSize: 18, fontWeight: 'bold', width: 40, textAlign: 'center', color: '#0f172a' },
  modalButtonsGroup: { flexDirection: 'row', justifyContent: 'flex-end', gap: 12 },
  saveBtn: { backgroundColor: '#e11d48', paddingVertical: 14, paddingHorizontal: 20, borderRadius: 12, cursor: 'pointer' },
  saveBtnText: { color: '#fff', fontSize: 16, fontWeight: 'bold' },
  cancelBtn: { backgroundColor: '#f1f5f9', paddingVertical: 14, paddingHorizontal: 20, borderRadius: 12, alignItems: 'center', cursor: 'pointer' },
  cancelBtnText: { color: '#475569', fontSize: 16, fontWeight: 'bold' }
});